import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, Check, AlertCircle } from 'lucide-react';
import { Logo } from '../components/Logo';
import Footer from '../components/Footer';

export default function ForgotPasswordPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post('/auth/forgot-password', { email });
            setSent(true);
        } catch { setSent(true); }
        finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <div className="border-b border-zinc-200 px-6 py-4">
                <div className="max-w-md mx-auto flex items-center gap-4">
                    <button onClick={() => navigate('/auth')} className="text-zinc-400 hover:text-zinc-900" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
                    <Logo size="md" data-testid="forgot-logo" />
                </div>
            </div>
            <div className="flex-1 flex items-start justify-center pt-12 px-6">
                <div className="w-full max-w-md">
                    {sent ? (
                        <div className="text-center" data-testid="reset-sent-msg">
                            <div className="w-16 h-16 bg-green-50 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-green-600" /></div>
                            <h2 className="font-heading text-2xl font-bold mb-2">Check Your Email</h2>
                            <p className="text-sm text-zinc-500 mb-6">If this email is registered, you will receive a password reset link shortly.</p>
                            <Button onClick={() => navigate('/auth')} className="rounded-none bg-zinc-900 text-white h-12" data-testid="back-to-login-btn">Back to Login</Button>
                        </div>
                    ) : (
                        <>
                            <h2 className="font-heading text-2xl font-bold mb-2">Forgot Password</h2>
                            <p className="text-sm text-zinc-500 mb-6">Enter your email and we'll send you a reset link.</p>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <Label className="text-sm font-semibold">Email</Label>
                                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="mt-1.5 h-14 rounded-none text-base" placeholder="your@email.com" required data-testid="reset-email-input" />
                                </div>
                                <Button type="submit" disabled={loading} className="w-full h-14 rounded-none bg-zinc-900 text-white text-base font-semibold" data-testid="reset-submit-btn">
                                    {loading ? 'Sending...' : 'Send Reset Link'}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}

export function ResetPasswordPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showPass, setShowPass] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) { setError('Passwords do not match'); return; }
        if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
        setLoading(true); setError('');
        try {
            await api.post('/auth/reset-password', { token, password });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Reset failed. Token may be expired.');
        } finally { setLoading(false); }
    };

    if (!token) return (
        <div className="min-h-screen bg-white flex items-center justify-center p-6">
            <div className="text-center"><AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" /><p className="text-zinc-600">Invalid reset link. No token provided.</p><Button onClick={() => navigate('/forgot-password')} className="mt-4 rounded-none" data-testid="request-new-reset-btn">Request a new reset</Button></div>
        </div>
    );

    if (success) return (
        <div className="min-h-screen bg-white flex items-center justify-center p-6">
            <div className="text-center" data-testid="reset-success"><Check className="w-12 h-12 text-green-600 mx-auto mb-4" /><h2 className="font-heading text-2xl font-bold mb-2">Password Reset Successfully</h2><p className="text-sm text-zinc-500 mb-6">Please log in with your new password.</p><Button onClick={() => navigate('/auth')} className="rounded-none bg-zinc-900 text-white h-12" data-testid="login-after-reset-btn">Go to Login</Button></div>
        </div>
    );

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <div className="border-b border-zinc-200 px-6 py-4"><div className="max-w-md mx-auto"><Logo size="md" data-testid="reset-logo" /></div></div>
            <div className="flex-1 flex items-start justify-center pt-12 px-6">
                <div className="w-full max-w-md">
                    <h2 className="font-heading text-2xl font-bold mb-2">Set New Password</h2>
                    <p className="text-sm text-zinc-500 mb-6">Enter your new password below.</p>
                    {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm mb-4" data-testid="reset-error">{error}</div>}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label className="text-sm font-semibold">New Password</Label>
                            <div className="relative mt-1.5">
                                <Input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="h-14 rounded-none text-base pr-12" required data-testid="new-password-input" />
                                <button type="button" onClick={() => setShowPass(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-900" data-testid="toggle-new-pass">{showPass ? '◉' : '○'}</button>
                            </div>
                        </div>
                        <div>
                            <Label className="text-sm font-semibold">Confirm Password</Label>
                            <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="mt-1.5 h-14 rounded-none text-base" required data-testid="confirm-password-input" />
                        </div>
                        <Button type="submit" disabled={loading} className="w-full h-14 rounded-none bg-zinc-900 text-white text-base font-semibold" data-testid="set-password-btn">{loading ? 'Resetting...' : 'Set New Password'}</Button>
                    </form>
                </div>
            </div>
            <Footer />
        </div>
    );
}
